import React from 'react';

import { SkipLink } from '@lumapps/a11y/components/SkipLink';
import { useClassnames } from '@lumapps/classnames';
import { homePageView } from '@lumapps/contents/routes';
import { InstanceSwitcher } from '@lumapps/instance-back-office/components/InstanceSwitcher';
import { useInstanceLogo } from '@lumapps/instance/hooks/useInstanceLogo';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { Spotlight } from '@lumapps/lumx-spotlight/components/Spotlight';
import { SpotlightFakeSearch } from '@lumapps/lumx-spotlight/components/SpotlightFakeSearch';
import { useSpotlightKeyboardShortcut } from '@lumapps/lumx-spotlight/hooks/useSpotlightKeyboardShortcut';
import { Alignment, FlexBox, Orientation, Size, Tooltip, type Theme } from '@lumapps/lumx/react';
import { Link as RouterLink } from '@lumapps/router';
import { useComputedTopStyles } from '@lumapps/style/hooks/useComputedTopStyles';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { UserAvatar } from '@lumapps/user-ui/components/UserAvatar';
import { useCurrentUser } from '@lumapps/user/hooks/useCurrentUser';

import { MAIN_CONTENT_ID, SCOPE } from '../../constants';

export interface NavigationHeaderProps {
    enabledFeatures?: Record<string, boolean>;
    /** Should the component be sticky to the top? */
    isSticky?: boolean;
    /** whether the search feature is enabled or not */
    isSpotlightEnabled?: boolean;
    /** background color override */
    backgroundColor?: string;
    children?: React.ReactNode;
    /** the instance name */
    siteName: string;
    /** parent instance id */
    parentInstance?: string;
    /** whether the nav inheritance FF is enabled or not */
    isMainNavInheritanceEnabled?: boolean;
    /** ref of the search field */
    searchButtonRef?: React.RefObject<HTMLAnchorElement>;
}

const CLASSNAME = 'navigation-header';

/**
 * Component displayed on the top of the side navigation in the back office. It should not be reused
 *
 * @family Back office
 */
export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
    enabledFeatures,
    backgroundColor,
    siteName,
    isSticky = false,
    children,
    parentInstance,
    isMainNavInheritanceEnabled,
    ...props
}) => {
    const { translateKey, translateAndReplace } = useTranslate();
    const topStyles = useComputedTopStyles({ parentInstance, isMainNavInheritanceEnabled });
    const { block, element } = useClassnames(CLASSNAME);
    const { user } = useCurrentUser();
    const instanceLogo = useInstanceLogo();
    const [isSpotlightOpen, , closeSpotlight, openSpotlight] = useBooleanState(false);
    const searchButtonRef = React.useRef<HTMLAnchorElement>(null);
    const isSideNavReworkEnabled = enabledFeatures?.sideNavRework;

    // Setup Ctrl+K / Cmd+K keyboard shortcut to open search dialog
    useSpotlightKeyboardShortcut(openSpotlight);

    return (
        <>
            <SkipLink anchorId={MAIN_CONTENT_ID} className={element('skip-link')} floating>
                {translateKey(GLOBAL.SKIP_TO_CONTENT)}
            </SkipLink>
            <li
                className={block({ 'is-sticky': isSticky, 'side-nav-rework-enabled': isSideNavReworkEnabled })}
                style={
                    !isSideNavReworkEnabled
                        ? { backgroundColor: topStyles.backgroundColor || backgroundColor }
                        : undefined
                }
                {...props}
            >
                <FlexBox orientation={Orientation.horizontal} hAlign={Alignment.center}>
                    {isSideNavReworkEnabled ? (
                        <InstanceSwitcher />
                    ) : (
                        <Tooltip label={translateKey(GLOBAL.VIEW_HOMEPAGE)}>
                            {/* Using target _self to force reload and switch to v1 or v2 if needed. */}
                            <RouterLink to={homePageView({})} target="_self" className={element('logo')}>
                                <img
                                    src={instanceLogo}
                                    alt={translateAndReplace(GLOBAL.HOME_PAGE, { site: siteName })}
                                />
                            </RouterLink>
                        </Tooltip>
                    )}

                    {!isSideNavReworkEnabled && (
                        <UserAvatar
                            user={user}
                            size={Size.m}
                            tooltipProps={{ label: translateKey(GLOBAL.VIEW_USER_PROFILE) }}
                            avatarAs="link"
                            buttonProps={{ 'aria-label': translateKey(GLOBAL.VIEW_USER_PROFILE) }}
                        />
                    )}

                    {children}
                </FlexBox>

                {/* Search button to trigger the dialog opening */}
                <SpotlightFakeSearch
                    buttonRef={searchButtonRef}
                    onClick={openSpotlight}
                    scope={SCOPE}
                    theme={!isSideNavReworkEnabled ? (topStyles.theme as Theme) : undefined}
                />
                {/* Search dialog */}
                <Spotlight isOpen={isSpotlightOpen} onClose={closeSpotlight} parentElement={searchButtonRef} />
            </li>
        </>
    );
};

NavigationHeader.displayName = 'SideNavigationItem';
